import { useEffect, useState } from "react";
import { api, triggerSync, formatApiError, fmtDate } from "@/lib/api";
import { outboxAll, outboxRemove, conflictsAll, conflictRemove } from "@/lib/offline";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { RefreshCw, Trash2, CloudUpload, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const SKIP = ["items", "activities", "customer_snapshot", "status_history", "updated_at", "created_at"];

const DiffTable = ({ mine, server }) => {
  const keys = Object.keys(mine || {}).filter((k) => !SKIP.includes(k) &&
    JSON.stringify(mine[k]) !== JSON.stringify((server || {})[k]));
  const itemsDiffer = JSON.stringify(mine?.items) !== JSON.stringify(server?.items);
  return (
    <table className="w-full text-xs border border-slate-200 rounded">
      <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
        <tr><th className="px-2 py-1.5 text-left w-1/4">Field</th><th className="px-2 py-1.5 text-left">Your offline version</th><th className="px-2 py-1.5 text-left">Server version</th></tr>
      </thead>
      <tbody>
        {keys.map((k) => (
          <tr key={k} className="border-t border-slate-100">
            <td className="px-2 py-1.5 font-medium text-slate-600">{k}</td>
            <td className="px-2 py-1.5 text-indigo-700">{String(JSON.stringify(mine[k])).slice(0, 90)}</td>
            <td className="px-2 py-1.5 text-slate-700">{String(JSON.stringify((server || {})[k] ?? "—")).slice(0, 90)}</td>
          </tr>
        ))}
        {itemsDiffer && (
          <tr className="border-t border-slate-100">
            <td className="px-2 py-1.5 font-medium text-slate-600">items</td>
            <td className="px-2 py-1.5 text-indigo-700">{(mine?.items || []).length} line item(s) — your edits</td>
            <td className="px-2 py-1.5 text-slate-700">{(server?.items || []).length} line item(s) — server</td>
          </tr>
        )}
        {keys.length === 0 && !itemsDiffer && (
          <tr><td colSpan={3} className="px-2 py-2 text-slate-400">No visible field differences (metadata changed)</td></tr>
        )}
      </tbody>
    </table>
  );
};

export default function SyncCenter() {
  const [queue, setQueue] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const load = () => { outboxAll().then(setQueue); conflictsAll().then(setConflicts); };
  useEffect(() => { load(); const h = () => load(); window.addEventListener("offline-sync", h); return () => window.removeEventListener("offline-sync", h); }, []);

  const applyMine = async (c) => {
    try {
      await api.request({ method: c.method, url: c.url, data: c.body,
        headers: { "X-Force-Sync": "1" }, _skipOffline: true });
      await conflictRemove(c.op_id);
      toast.success("Your offline version applied");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const keepServer = async (c) => {
    await conflictRemove(c.op_id);
    toast.success("Server version kept — offline change discarded");
    load();
  };
  const discardOp = async (op) => {
    await outboxRemove(op.op_id);
    toast.success("Queued change discarded");
    load();
  };

  return (
    <div className="space-y-5" data-testid="sync-center">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PageHeader title="Sync Center" subtitle="Offline changes waiting to sync, and conflicts that need your decision" />
        <Button onClick={() => triggerSync()} variant="outline" className="gap-1.5" data-testid="sync-now-btn">
          <RefreshCw size={14} /> Sync now
        </Button>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2"><CloudUpload size={14} /> Queued changes ({queue.length})</h3>
        <div className="space-y-2">
          {queue.map((op) => (
            <div key={op.op_id} className="border border-slate-200 rounded-md px-4 py-2.5 flex items-center gap-3 text-sm bg-white" data-testid={`sync-op-${op.op_id}`}>
              <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">{op.method}</span>
              <span className="font-mono text-xs text-slate-600 flex-1 truncate">{op.url}</span>
              <span className="text-[11px] text-slate-400">{fmtDate(new Date(op.queued_at).toISOString())}</span>
              <button onClick={() => discardOp(op)} className="text-slate-400 hover:text-red-500" title="Discard this change" data-testid={`sync-discard-${op.op_id}`}><Trash2 size={13} /></button>
            </div>
          ))}
          {queue.length === 0 && <p className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-md px-4 py-6 text-center">Nothing queued — all changes are on the server.</p>}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2"><AlertTriangle size={14} className="text-rose-500" /> Conflicts ({conflicts.length})</h3>
        <div className="space-y-3">
          {conflicts.map((c) => (
            <div key={c.op_id} className="border border-rose-200 rounded-md p-4 bg-white space-y-3" data-testid={`sync-conflict-${c.op_id}`}>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">{c.method}</span>
                <span className="font-mono text-xs text-slate-600">{c.url}</span>
                {c.server?.number && <span className="text-xs text-slate-500">({c.server.number})</span>}
              </div>
              {c.error && !c.server ? (
                <p className="text-xs text-rose-600">Sync failed: {typeof c.error === "string" ? c.error : JSON.stringify(c.error)}</p>
              ) : (
                <DiffTable mine={c.body} server={c.server} />
              )}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => keepServer(c)} className="gap-1" data-testid={`conflict-keep-server-${c.op_id}`}>
                  <CheckCircle2 size={13} /> Keep server version
                </Button>
                <Button size="sm" onClick={() => applyMine(c)} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white gap-1" data-testid={`conflict-apply-mine-${c.op_id}`}>
                  <CloudUpload size={13} /> Apply my offline version
                </Button>
              </div>
            </div>
          ))}
          {conflicts.length === 0 && <p className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-md px-4 py-6 text-center">No conflicts — everything synced cleanly.</p>}
        </div>
      </div>
    </div>
  );
}
